import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { WithWrite } from '../../lib/rbac';
import { settingsApi, type Printer } from '../../services/settings';
import { TableWrapper, TableHead } from '../../components/TableWrapper';

export function PrintersPage() {
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Printer | null>(null);
  const [form, setForm] = useState({ name: '', placement: '', ipAddress: '', notes: '' });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const { error, success, info } = useToast();
  const { canWrite } = useAuth();
  const colCount = canWrite('settings') ? 6 : 5;

  const load = useCallback(async () => {
    try {
      const res = await settingsApi.getPrinters();
      setPrinters(res.printers);
    } catch (e) {
      error(e instanceof Error ? e.message : 'Kunde inte hämta skrivare');
    } finally {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!pendingDeleteId) return;
    const t = setTimeout(() => setPendingDeleteId(null), 5000);
    return () => clearTimeout(t);
  }, [pendingDeleteId]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      error('Namn krävs');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await settingsApi.updatePrinter(editing.id, {
          name: form.name,
          placement: form.placement || undefined,
          ipAddress: form.ipAddress || undefined,
          notes: form.notes || undefined,
        });
        success('Skrivare uppdaterad');
      } else {
        await settingsApi.createPrinter({
          name: form.name,
          placement: form.placement || undefined,
          ipAddress: form.ipAddress || undefined,
          notes: form.notes || undefined,
        });
        success('Skrivare tillagd');
      }
      setEditing(null);
      setShowForm(false);
      setForm({ name: '', placement: '', ipAddress: '', notes: '' });
      load();
    } catch (e) {
      error(e instanceof Error ? e.message : 'Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (p: Printer) => {
    setEditing(p);
    setForm({
      name: p.name,
      placement: p.placement ?? '',
      ipAddress: p.ipAddress ?? '',
      notes: p.notes ?? '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (pendingDeleteId !== id) {
      setPendingDeleteId(id);
      info('Klicka igen för att ta bort skrivaren');
      return;
    }
    try {
      await settingsApi.deletePrinter(id);
      success('Skrivare borttagen');
      setPendingDeleteId(null);
      load();
    } catch (e) {
      error(e instanceof Error ? e.message : 'Kunde inte ta bort');
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Laddar...</p>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">Skrivare</h2>
        <WithWrite moduleCode="settings">
          <button
            type="button"
            className="px-2 py-1 text-sm bg-primary text-white rounded hover:bg-primary-hover"
            onClick={() => {
              setEditing(null);
              setForm({ name: '', placement: '', ipAddress: '', notes: '' });
              setShowForm(true);
            }}
          >
            Lägg till skrivare
          </button>
        </WithWrite>
      </div>
      {showForm && (
        <div className="border border-gray-200 rounded p-3 bg-gray-50 space-y-2 max-w-md">
          <input
            type="text"
            className="w-full border rounded px-2 py-1 text-sm"
            placeholder="Namn *"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <input
            type="text"
            className="w-full border rounded px-2 py-1 text-sm"
            placeholder="Placering"
            value={form.placement}
            onChange={(e) => setForm((f) => ({ ...f, placement: e.target.value }))}
          />
          <input
            type="text"
            className="w-full border rounded px-2 py-1 text-sm"
            placeholder="IP-adress"
            value={form.ipAddress}
            onChange={(e) => setForm((f) => ({ ...f, ipAddress: e.target.value }))}
          />
          <input
            type="text"
            className="w-full border rounded px-2 py-1 text-sm"
            placeholder="Anteckningar"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
          <div className="flex gap-2">
            <button type="button" className="px-2 py-1 text-sm bg-primary text-white rounded" onClick={handleSave} disabled={saving}>
              {saving ? 'Sparar...' : 'Spara'}
            </button>
            <button type="button" className="px-2 py-1 text-sm border rounded" onClick={() => { setShowForm(false); setEditing(null); }}>
              Avbryt
            </button>
          </div>
        </div>
      )}
      <TableWrapper>
        <table className="w-full text-sm border-collapse">
          <TableHead>
            <tr>
              <th className="text-left p-1.5 font-medium">Namn</th>
              <th className="text-left p-1.5 font-medium">Placering</th>
              <th className="text-left p-1.5 font-medium">IP</th>
              <th className="text-left p-1.5 font-medium">Anteckningar</th>
              <th className="text-left p-1.5 font-medium">Aktiv</th>
              <WithWrite moduleCode="settings">
                <th className="text-left p-1.5 font-medium">Åtgärder</th>
              </WithWrite>
            </tr>
          </TableHead>
          <tbody>
            {printers.length === 0 ? (
              <tr><td colSpan={colCount} className="p-2 text-gray-500">Inga skrivare. Lägg till under Inställningar.</td></tr>
            ) : (
              printers.map((p) => (
                <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="p-1.5">{p.name}</td>
                  <td className="p-1.5">{p.placement ?? '-'}</td>
                  <td className="p-1.5">{p.ipAddress ?? '-'}</td>
                  <td className="p-1.5">{p.notes ?? '-'}</td>
                  <td className="p-1.5">{p.isActive ? 'Ja' : 'Nej'}</td>
                  <WithWrite moduleCode="settings">
                    <td className="p-1.5">
                      <button type="button" className="text-primary text-sm hover:underline mr-2" onClick={() => handleEdit(p)}>Redigera</button>
                      <button type="button" className="text-red-600 text-sm hover:underline" onClick={() => handleDelete(p.id)}>
                        {pendingDeleteId === p.id ? 'Bekräfta ta bort' : 'Ta bort'}
                      </button>
                    </td>
                  </WithWrite>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </TableWrapper>
    </div>
  );
}

